import React, { useState, useEffect } from 'react';
import { IModuleWithPermissions } from '@/types/roles-permissions';
import { Button } from '@/components/button/Buttons';
import { RolesService } from '@/services/roles/rolesService';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/card/card';

const RolePermissionsPage: React.FC = () => {
  const [modulePermissions, setModulePermissions] = useState<
    IModuleWithPermissions[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedModules, setExpandedModules] = useState<{
    [key: string]: boolean;
  }>({});
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchRoleAndPermissions = async () => {
      if (!roleId) {
        enqueueSnackbar('Invalid role ID', { variant: 'error' });
        navigate('/roles-management');
        return;
      }

      try {
        setLoading(true);

        // First fetch all permissions
        const allPermissionsResponse = await RolesService.getAllPermissions(
          1,
          1000
        );
        const allModulePermissions =
          allPermissionsResponse.modulePermissions || [];

        // Then fetch role-specific permissions
        const rolePermissionsResponse =
          await RolesService.getRoleWithPermissions(roleId);
        const roleModulePermissions =
          rolePermissionsResponse.modulePermissions || [];

        // Merge permissions: use all permissions as base, and mark role permissions as checked
        const mergedModulePermissions = allModulePermissions.map(
          (allModule) => {
            const roleModule = roleModulePermissions.find(
              (rm) => rm.module.id === allModule.module.id
            );

            if (
              roleModule &&
              roleModule.rawPermissions &&
              allModule.rawPermissions
            ) {
              // Merge raw permissions
              const mergedRawPermissions = allModule.rawPermissions.map(
                (allPerm) => {
                  const rolePerm = roleModule.rawPermissions?.find(
                    (rp) => rp.id === allPerm.id
                  );
                  return rolePerm ? rolePerm : allPerm;
                }
              );

              return {
                ...allModule,
                rawPermissions: mergedRawPermissions,
              };
            } else if (
              roleModule &&
              !roleModule.rawPermissions &&
              !allModule.rawPermissions
            ) {
              // Merge permissions objects
              const mergedPermissions = { ...allModule.permissions };
              Object.keys(roleModule.permissions).forEach((key) => {
                if (roleModule.permissions[key]) {
                  mergedPermissions[key] = true;
                }
              });

              return {
                ...allModule,
                permissions: mergedPermissions,
              };
            }

            return allModule;
          }
        );

        setModulePermissions(mergedModulePermissions);
      } catch (error: unknown) {
        const err = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          err?.response?.data?.message || err?.message || 'An error occurred';

        enqueueSnackbar(errorMessage, { variant: 'error' });
        setModulePermissions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRoleAndPermissions();
  }, [roleId, enqueueSnackbar, navigate]);

  const handlePermissionChange = (
    moduleId: string,
    permissionKey: string,
    isChecked: boolean
  ) => {
    setModulePermissions((prevModules) =>
      prevModules.map((moduleItem) => {
        if (moduleItem.module.id === moduleId) {
          if (moduleItem.rawPermissions) {
            const updatedRawPermissions = moduleItem.rawPermissions.map(
              (perm) => {
                if (perm.id === permissionKey) {
                  return {
                    ...perm,
                    role_has_permissions: isChecked
                      ? [{ id: `temp-${perm.id}`, role_id: roleId || '' }]
                      : [],
                  };
                }
                return perm;
              }
            );

            return {
              ...moduleItem,
              rawPermissions: updatedRawPermissions,
            };
          } else {
            return {
              ...moduleItem,
              permissions: {
                ...moduleItem.permissions,
                [permissionKey]: isChecked,
              },
            };
          }
        }
        return moduleItem;
      })
    );
  };

  const handleSavePermissions = async () => {
    if (!roleId) {
      enqueueSnackbar('Invalid role ID', { variant: 'error' });
      return;
    }

    try {
      setSaving(true);

      // Prepare permissions data to save
      const permissionsToSave = modulePermissions.flatMap((moduleItem) => {
        const moduleId = moduleItem.module.id;

        if (moduleItem.rawPermissions) {
          return moduleItem.rawPermissions.map((perm) => ({
            moduleId,
            permissionId: perm.id,
            isGranted: Boolean(
              perm.role_has_permissions && perm.role_has_permissions.length > 0
            ),
          }));
        } else {
          return Object.entries(moduleItem.permissions)
            .filter(([key]) => !key.endsWith('_info'))
            .map(([key, value]) => ({
              moduleId,
              permissionId: key,
              isGranted: Boolean(value),
            }));
        }
      });

      // Use the service to save permissions
      await RolesService.saveRolePermissions(roleId, permissionsToSave);

      enqueueSnackbar('Permissions saved successfully', { variant: 'success' });
      navigate('/roles-management');
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save permissions';

      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAllPermissions = (moduleId: string, isChecked: boolean) => {
    setModulePermissions((prevModules) =>
      prevModules.map((moduleItem) => {
        if (moduleItem.module.id === moduleId) {
          if (moduleItem.rawPermissions) {
            const updatedRawPermissions = moduleItem.rawPermissions.map(
              (perm) => ({
                ...perm,
                role_has_permissions: isChecked
                  ? [{ id: `temp-${perm.id}`, role_id: roleId || '' }]
                  : [],
              })
            );

            return {
              ...moduleItem,
              rawPermissions: updatedRawPermissions,
            };
          } else {
            return {
              ...moduleItem,
              permissions: Object.keys(moduleItem.permissions)
                .filter((key) => !key.endsWith('_info'))
                .reduce(
                  (acc, key) => ({
                    ...acc,
                    [key]: isChecked,
                  }),
                  {}
                ),
            };
          }
        }
        return moduleItem;
      })
    );
  };

  // Check if all permissions in a module are selected
  const areAllPermissionsSelected = (
    moduleItem: IModuleWithPermissions
  ): boolean => {
    if (moduleItem.rawPermissions) {
      return moduleItem.rawPermissions.every(
        (perm) =>
          perm.role_has_permissions && perm.role_has_permissions.length > 0
      );
    } else {
      return Object.entries(moduleItem.permissions)
        .filter(([key]) => !key.endsWith('_info'))
        .every(([, value]) => Boolean(value));
    }
  };

  const handleGoBack = () => {
    navigate('/roles-management');
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  // Initialize expanded state for all modules on load (only once)
  useEffect(() => {
    if (
      modulePermissions.length > 0 &&
      Object.keys(expandedModules).length === 0
    ) {
      const initialExpandState = modulePermissions.reduce(
        (acc, module) => {
          acc[module.module.id] = false; // Default to collapsed
          return acc;
        },
        {} as { [key: string]: boolean }
      );

      setExpandedModules(initialExpandState);
    }
  }, [modulePermissions, expandedModules]);

  // Filter modules and permissions based on search term
  const filteredModulePermissions = modulePermissions
    .map((moduleItem) => {
      const moduleNameMatch = moduleItem.module.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      let filteredPermissions: [string, boolean | { name: string }][] = [];
      let filteredRawPermissions: unknown[] = [];

      if (moduleItem.rawPermissions) {
        filteredRawPermissions = moduleItem.rawPermissions.filter((perm) =>
          perm.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        filteredPermissions = Object.entries(moduleItem.permissions)
          .filter(([key]) => !key.endsWith('_info'))
          .filter(([key, value]) => {
            const permissionName =
              typeof value === 'object' && value && 'name'
                ? (value as { name: string }).name
                : key.replace(/_/g, ' ');
            return permissionName
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
          })
          .map(([key, value]) => [key, value as boolean | { name: string }]);
      }

      // Show module if module name matches or has matching permissions
      if (
        moduleNameMatch ||
        filteredRawPermissions.length > 0 ||
        filteredPermissions.length > 0
      ) {
        return {
          ...moduleItem,
          rawPermissions:
            filteredRawPermissions.length > 0
              ? filteredRawPermissions
              : moduleItem.rawPermissions,
          permissions:
            filteredPermissions.length > 0
              ? Object.fromEntries(filteredPermissions)
              : moduleItem.permissions,
        };
      }

      return null;
    })
    .filter(Boolean) as IModuleWithPermissions[];

  return (
    <>
      <Helmet>
        <title>Edit Role Permissions</title>
      </Helmet>
      <div className="container mx-auto py-8 px-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Edit Role Permissions
            </h1>
          </div>
          <div>
            <Button variant="secondary" onClick={handleGoBack}>
              Back to Roles
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading permissions...</p>
            </div>
          </div>
        ) : !roleId ? (
          <div className="p-10 text-center">
            <p className="text-gray-500">Role not found</p>
          </div>
        ) : modulePermissions.length === 0 ? (
          <Card className="overflow-hidden shadow-lg border-0 p-10">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="mt-2 text-gray-500">
                  No permissions found for this role
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  Try refreshing or contact an administrator
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Tree structure of permissions similar to the image */}
            <Card className="overflow-hidden shadow-lg border-0">
              <div className="bg-gray-100 p-6">
                <div className="flex justify-between items-center">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      Role Permissions
                    </h3>
                    <p className="text-gray-600">
                      Select the permissions you want to grant to this role.
                    </p>
                  </div>

                  {/* Search Input */}
                  <div className="mb-6">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className="block w-full input pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search permissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {filteredModulePermissions.map((moduleItem) => {
                    const hasPermissions = moduleItem.rawPermissions
                      ? moduleItem.rawPermissions.length > 0
                      : Object.keys(moduleItem.permissions).filter(
                          (key) => !key.endsWith('_info')
                        ).length > 0;

                    if (!hasPermissions) return null;

                    return (
                      <div
                        key={moduleItem.module.id}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div
                          className="bg-gray-200 p-4 flex items-center justify-between cursor-pointer"
                          onClick={() =>
                            toggleModuleExpansion(moduleItem.module.id)
                          }
                        >
                          <div className="flex items-center">
                            <input
                              id={`module-${moduleItem.module.id}`}
                              type="checkbox"
                              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                              checked={areAllPermissionsSelected(moduleItem)}
                              onChange={(e) => {
                                handleSelectAllPermissions(
                                  moduleItem.module.id,
                                  e.target.checked
                                );
                              }}
                            />
                            <label
                              htmlFor={`module-${moduleItem.module.id}`}
                              className="ml-3 text-lg font-medium text-gray-900 cursor-pointer"
                            >
                              {moduleItem.module.name}
                            </label>
                          </div>

                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-700 focus:outline-none p-1"
                            aria-label={
                              expandedModules[moduleItem.module.id]
                                ? `Collapse ${moduleItem.module.name} permissions`
                                : `Expand ${moduleItem.module.name} permissions`
                            }
                            title={
                              expandedModules[moduleItem.module.id]
                                ? `Collapse ${moduleItem.module.name} permissions`
                                : `Expand ${moduleItem.module.name} permissions`
                            }
                            aria-expanded={Boolean(
                              expandedModules[moduleItem.module.id]
                            )}
                            aria-controls={`module-panel-${moduleItem.module.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleModuleExpansion(moduleItem.module.id);
                            }}
                          >
                            {expandedModules[moduleItem.module.id] ? (
                              <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                                focusable="false"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                                focusable="false"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                          </button>
                        </div>

                        {/* Module permissions */}
                        {expandedModules[moduleItem.module.id] && (
                          <div
                            id={`module-panel-${moduleItem.module.id}`}
                            className="pl-10 divide-y divide-gray-200"
                          >
                            {moduleItem.rawPermissions
                              ? moduleItem.rawPermissions.map((permission) => (
                                  <div
                                    key={permission.id}
                                    className="py-3 px-4 flex items-center justify-between"
                                  >
                                    <div className="flex items-center">
                                      <input
                                        id={`permission-${permission.id}`}
                                        type="checkbox"
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                        checked={
                                          permission.role_has_permissions &&
                                          permission.role_has_permissions
                                            .length > 0
                                        }
                                        onChange={(e) => {
                                          handlePermissionChange(
                                            moduleItem.module.id,
                                            permission.id,
                                            e.target.checked
                                          );
                                        }}
                                      />
                                      <label
                                        htmlFor={`permission-${permission.id}`}
                                        className="ml-3 text-base text-gray-700 cursor-pointer"
                                      >
                                        {permission.name}
                                      </label>
                                    </div>
                                  </div>
                                ))
                              : Object.entries(moduleItem.permissions)
                                  .filter(([key]) => !key.endsWith('_info'))
                                  .map(([key, value]) => (
                                    <div
                                      key={key}
                                      className="py-3 px-4 flex items-center justify-between"
                                    >
                                      <div className="flex items-center">
                                        <input
                                          id={`permission-${key}`}
                                          type="checkbox"
                                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                                          checked={
                                            typeof value === 'boolean'
                                              ? value
                                              : false
                                          }
                                          onChange={(e) => {
                                            handlePermissionChange(
                                              moduleItem.module.id,
                                              key,
                                              e.target.checked
                                            );
                                          }}
                                        />
                                        <label
                                          htmlFor={`permission-${key}`}
                                          className="ml-3 text-base text-gray-700 cursor-pointer"
                                        >
                                          {typeof value === 'object' &&
                                          value &&
                                          'name' in value
                                            ? (value as { name: string }).name
                                            : key.replace(/_/g, ' ')}
                                        </label>
                                      </div>
                                    </div>
                                  ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>

            {/* Save/Cancel buttons */}
            <div className="flex justify-end mt-6 gap-5">
              <Button variant="secondary" onClick={handleGoBack}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSavePermissions}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save Permissions'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default RolePermissionsPage;
